import { useEffect } from 'react';
import { ThemeTokens } from '@repo/types';

// Map theme token keys to CSS variable names.
// `fontFamilyBase` is intentionally omitted: the base font is owned by each
// tenant's theme.scss (compiled to theme.css) and must not be overridable via
// a remote theme token, so it is never applied as an inline style here.
const tokenToCssVar: Partial<Record<keyof ThemeTokens, string>> = {
  // Colors - Backgrounds
  colorBackgroundPrimaryLight: '--color-background-primary-light',
  colorBackgroundSecondaryLight: '--color-background-secondary-light',
  colorBackgroundAlternative: '--color-background-alternative',
  colorBackgroundHeader: '--color-background-header',
  colorHeaderAccent: '--color-header-accent',
  // Colors - Text
  colorTextPrimaryOnLight: '--color-text-primary-on-light',
  colorTextSecondaryOnLight: '--color-text-secondary-on-light',
  colorTextPrimaryOnDark: '--color-text-primary-on-dark',
  colorTextSecondaryOnDark: '--color-text-secondary-on-dark',
  colorTextMainNavHover: '--color-text-main-nav-hover',
  colorTextSecondaryNavHover: '--color-text-secondary-nav-hover',
  // Colors - Highlights
  colorHighlightPrimary: '--color-highlight-primary',
  colorHighlightPrimaryHover: '--color-highlight-primary-hover',
  colorHighlightSecondary: '--color-highlight-secondary',
  // Colors - League Table Zones
  colorLeagueTablePromotion: '--color-league-table-promotion',
  colorLeagueTablePlayOffs: '--color-league-table-play-offs',
  colorLeagueTableRelegation: '--color-league-table-relegation',
  // Colors - Buttons
  colorButtonPrimary: '--color-button-primary',
  colorButtonPrimaryHover: '--color-button-primary-hover',
  colorButtonSecondary: '--color-button-secondary',
  colorButtonSecondaryHover: '--color-button-secondary-hover',
  // Colors - Form Inputs
  colorBackgroundInput: '--color-background-input',
  colorInputBorder: '--color-input-border',
  colorInputBorderFocused: '--color-input-border-focused',
  colorInputError: '--color-input-error',
  // Typography Sizes
  size3xs: '--size-3xs',
  size2xs: '--size-2xs',
  sizeXs: '--size-xs',
  sizeS: '--size-s',
  sizeM: '--size-m',
  sizeL: '--size-l',
  sizeXl: '--size-xl',
  size2xl: '--size-2xl',
  size3xl: '--size-3xl',
  size4xl: '--size-4xl',
  // Spacing
  spacing1: '--spacing-1',
  spacing2: '--spacing-2',
  spacing3: '--spacing-3',
  spacing4: '--spacing-4',
  spacing5: '--spacing-5',
  spacing6: '--spacing-6',
  spacing7: '--spacing-7',
  spacing8: '--spacing-8',
  spacing9: '--spacing-9',
  spacing10: '--spacing-10',
  spacing11: '--spacing-11',
  spacing12: '--spacing-12',
  spacing13: '--spacing-13',
  spacing14: '--spacing-14',
  spacing15: '--spacing-15',
  // Border Radius
  borderRadiusXs: '--border-radius-xs',
  borderRadiusS: '--border-radius-s',
  borderRadiusM: '--border-radius-m',
  borderRadiusL: '--border-radius-l',
  borderRadiusXl: '--border-radius-xl',
  borderRadiusRound: '--border-radius-round',
  // Shadows
  shadow1: '--shadow-1',
  // Colors - Chart Series
  colorChartSeries1: '--color-chart-series-1',
  colorChartSeries1Ink: '--color-chart-series-1-ink',
  colorChartSeries2: '--color-chart-series-2',
  colorChartSeries2Ink: '--color-chart-series-2-ink',
  colorChartSeries3: '--color-chart-series-3',
  colorChartSeries3Ink: '--color-chart-series-3-ink',
  colorChartSeries4: '--color-chart-series-4',
  colorChartSeries4Ink: '--color-chart-series-4-ink',
  // Colors - Chart Semantic Markers
  colorStatDeltaUp: '--color-stat-delta-up',
  colorStatDeltaDown: '--color-stat-delta-down',
  colorChartAverage: '--color-chart-average',
  colorChartInfo: '--color-chart-info',
};

/**
 * Applies theme tokens as inline CSS variables on the document root.
 *
 * IMPORTANT: This function only applies tokens that are explicitly provided.
 * If theme is null/undefined, no inline styles are set, allowing the CSS
 * variables from the tenant's theme.scss (compiled to theme.css) to take effect.
 *
 * This prevents the "flash" issue where default theme colors would briefly
 * appear before the remote theme tokens are fetched.
 */
export function applyTheme(theme: ThemeTokens | null | undefined) {
  if (!theme) {
    return;
  }

  const root = document.documentElement;

  Object.entries(theme).forEach(([key, value]) => {
    const cssVar = tokenToCssVar[key as keyof ThemeTokens];
    if (cssVar && value) {
      root.style.setProperty(cssVar, value);
    }
  });
}

export function useTheme(theme: ThemeTokens | null | undefined) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    applyTheme(theme);
  }, [theme]);
}
